import { Injectable, inject, signal } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

/**
 * Responsive layout service for handling different screen sizes
 * Uses Angular CDK BreakpointObserver to detect viewport changes
 */
@Injectable({
  providedIn: 'root'
})
export class ResponsiveLayoutService {
  private breakpointObserver = inject(BreakpointObserver);

  /**
   * Observable that emits current screen size category
   */
  private screenSize$ = this.breakpointObserver
    .observe([
      Breakpoints.XSmall,    // < 600px (mobile)
      Breakpoints.Small,     // 600px - 959px (tablet)
      Breakpoints.Medium,    // 960px - 1279px (laptop)
      Breakpoints.Large,     // 1280px - 1919px (desktop)
      Breakpoints.XLarge     // >= 1920px (large desktop)
    ])
    .pipe(
      map(result => {
        if (result.breakpoints[Breakpoints.XSmall]) {
          return 'mobile';
        } else if (result.breakpoints[Breakpoints.Small]) {
          return 'tablet';
        } else if (result.breakpoints[Breakpoints.Medium]) {
          return 'laptop';
        } else if (result.breakpoints[Breakpoints.Large]) {
          return 'desktop';
        } else {
          return 'large-desktop';
        }
      })
    );

  /**
   * Signal containing current screen size
   */
  screenSize = toSignal(this.screenSize$, { initialValue: 'desktop' });

  /**
   * Check if current viewport is mobile (< 600px)
   */
  isMobile$ = this.breakpointObserver
    .observe([Breakpoints.XSmall])
    .pipe(map(result => result.matches));

  isMobile = toSignal(this.isMobile$, { initialValue: false });

  /**
   * Check if current viewport is tablet (600px - 959px)
   */
  isTablet$ = this.breakpointObserver
    .observe([Breakpoints.Small])
    .pipe(map(result => result.matches));

  isTablet = toSignal(this.isTablet$, { initialValue: false });

  /**
   * Check if current viewport is mobile or tablet (< 960px)
   */
  isMobileOrTablet$ = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(map(result => result.matches));

  isMobileOrTablet = toSignal(this.isMobileOrTablet$, { initialValue: false });

  /**
   * Check if current viewport is desktop or larger (>= 960px)
   */
  isDesktop$ = this.breakpointObserver
    .observe([Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
    .pipe(map(result => result.matches));

  isDesktop = toSignal(this.isDesktop$, { initialValue: true });

  /**
   * Custom breakpoint matcher
   * @param query CSS media query string
   * @returns Observable of match result
   */
  matchCustomBreakpoint(query: string) {
    return this.breakpointObserver
      .observe(query)
      .pipe(map(result => result.matches));
  }

  /**
   * Get current viewport width
   */
  getViewportWidth(): number {
    return window.innerWidth;
  }

  /**
   * Get current viewport height
   */
  getViewportHeight(): number {
    return window.innerHeight;
  }
}
